import { Avatar, Box, Card, CardContent, Stack } from '@mui/material'
import { getIsDoctor } from 'common/utils'
import CustomText from 'components/common/CustomText/CustomText'
import { colors } from 'styles/colors'
import { useMemo } from 'react'
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize'
import { useAcceptInvitation, useRejectInvitation } from 'hooks/useUsers'
import { useAuth } from 'context/AuthContext'
import { snackbarService } from 'services/snackbarService'
import CustomButton from 'components/buttons/CustomButton/CustomButton'

interface Props {
  item: any;
  refetch?: () => void;
}

export default function DashboardInvitationCard({ item, refetch }: Props) {
  const { user } = useAuth();

  const isDoctor = useMemo(() => getIsDoctor(item.user_role?.name), [item]);
  const doctorId = item.user_profile?.[0]?.user;

  const { mutate: acceptMutation, isLoading: isAccepting } = useAcceptInvitation(
    () => {
      snackbarService.showSnackbar('Invitation has been accepted successfully', 'success');
      refetch?.();
    },
    (err: any) => {
      snackbarService.showSnackbar(err?.message ?? err, 'error');
    },
  );

  const { mutate: rejectMutation, isLoading: isRejecting } = useRejectInvitation(
    () => {
      snackbarService.showSnackbar('Invitation has been rejected successfully', 'success');
      refetch?.();
    },
    (err: any) => {
      snackbarService.showSnackbar(err?.message ?? err, 'error');
    },
  );

  const handleAccept = () => {
    acceptMutation({ userId: user.id, doctorId });
  };

  const handleReject = () => {
    rejectMutation({ userId: user.id, doctorId });
  };

  return (
    <Card
      sx={{
        borderRadius: 3,
        boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
        minHeight: 180,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardContent
        sx={{
          p: 2,
          '&:last-child': { pb: 2 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Top row */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ position: 'relative', display: 'inline-block' }}>
            <Avatar
              src={item?.user_media?.image?.href_small}
              alt="profile"
              sx={{ width: 38, height: 38, bgcolor: colors.avatarBg }}
            />
            <Box
              sx={{
                position: 'absolute',
                bottom: -2,
                right: -2,
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: colors.white,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 5px 12px rgba(0,0,0,0.25)',
              }}
            >
              <DashboardCustomizeIcon sx={{ fontSize: 10, color: colors.link }} />
            </Box>
          </Box>

          <CustomText
            text={`${isDoctor ? 'Dr. ' : ''}${item.name}`}
            color={colors.primary}
            fontSize={18}
            fontWeight={600}
            noWrap
          />
        </Stack>

        <Box mt={1}>
          <CustomText
            text={`${isDoctor ? 'Dr. ' : ''}${item.name} has invited you to access their dashboard.`}
            color={colors.secondary}
            fontSize={15}
          />
        </Box>

        <Box mt={'auto'}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <CustomButton
              key="reject"
              variant="outlined"
              intent="error"
              text="Reject"
              onClick={handleReject}
              fullWidth
              height={35}
              disabled={isRejecting || isAccepting}
              loading={isRejecting}
            />
            <CustomButton
              key="accept"
              variant="filled"
              color="success"
              text="Accept"
              onClick={handleAccept}
              fullWidth
              height={35}
              disabled={isRejecting || isAccepting}
              loading={isAccepting}
            />
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
